// SNIGJ - Police Work Page (Interpellations, Gardes à vue, Libérations)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  UserSearch, HandMetal, Clock, Unlock, Shield, Search,
  RefreshCw, AlertTriangle, CheckCircle, User, MapPin, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import PoliceAssistant from '../components/assistant/PoliceAssistant';

const PoliceWorkPage = () => {
  const { user, language } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'interpellations';

  const [interpellations, setInterpellations] = useState([]);
  const [detentions, setDetentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAssistant, setShowAssistant] = useState(false);

  const isSupervisor = ['PROCUREUR_GENERAL', 'PROCUREUR_REPUBLIQUE', 'SUBSTITUT_PROCUREUR', 'COMMISSAIRE_PRINCIPAL', 'SUPER_ADMIN'].includes(user?.role);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [interpRes, detRes] = await Promise.all([
        api.get('/police/interpellations'),
        api.get('/police/detentions')
      ]);
      setInterpellations(interpRes.data || []);
      setDetentions(detRes.data || []);
    } catch (err) {
      console.error('Failed to load police data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (detentionId) => {
    try {
      await api.post(`/hierarchy/detention/${detentionId}/confirm`);
      toast.success('Garde à vue confirmée');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleRelease = async (detentionId, personName) => {
    const motif = prompt(`Motif de libération de ${personName} :`);
    if (!motif) return;
    try {
      await api.post(`/hierarchy/detention/${detentionId}/release`, { motif });
      toast.success('Libération ordonnée');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const filterItems = (items) => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item =>
      (item.person_name || '').toLowerCase().includes(q) ||
      (item.motif || '').toLowerCase().includes(q) ||
      (item.agent_name || '').toLowerCase().includes(q)
    );
  };

  const activeDetentions = detentions.filter(d => d.status === 'EN_COURS');
  const releasedDetentions = detentions.filter(d => d.status !== 'EN_COURS');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="police-work-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              {language === 'fr' ? 'Travail Police / OPJ' : 'Police / OPJ Work'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr' ? 'Interpellations, gardes à vue et libérations' : 'Apprehensions, detentions and releases'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 w-60"
                data-testid="police-search"
              />
            </div>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => setShowAssistant(true)} className="gap-2" data-testid="open-assistant-btn">
              <Shield className="w-4 h-4" />
              {language === 'fr' ? 'Nouvelle action' : 'New action'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{interpellations.length}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Interpellations' : 'Apprehensions'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{activeDetentions.length}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Gardes à vue actives' : 'Active detentions'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{activeDetentions.filter(d => !d.confirmed).length}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'En attente confirmation' : 'Pending confirmation'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{releasedDetentions.length}</p>
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Libérés' : 'Released'}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue={defaultTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="interpellations" className="gap-1">
              <UserSearch className="w-4 h-4" />
              {language === 'fr' ? 'Interpellations' : 'Apprehensions'}
              <Badge className="ml-1 text-[10px]">{interpellations.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="detentions" className="gap-1">
              <HandMetal className="w-4 h-4" />
              {language === 'fr' ? 'Gardes à vue' : 'Detentions'}
              {activeDetentions.filter(d => !d.confirmed).length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-[10px]">{activeDetentions.filter(d => !d.confirmed).length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="liberations" className="gap-1">
              <Unlock className="w-4 h-4" />
              {language === 'fr' ? 'Libérations' : 'Releases'}
            </TabsTrigger>
          </TabsList>

          {/* Interpellations */}
          <TabsContent value="interpellations">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground">Chargement...</div>
                ) : filterItems(interpellations).length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <UserSearch className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    {language === 'fr' ? 'Aucune interpellation' : 'No apprehensions'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filterItems(interpellations).map((item) => (
                      <div key={item.interpellation_id} className="p-4 hover:bg-muted/50" data-testid={`interp-${item.interpellation_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">{item.person_name}</p>
                              <p className="text-xs text-muted-foreground">{item.motif}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="text-xs">{item.status || 'ACTIVE'}</Badge>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 justify-end">
                              <Calendar className="w-3 h-3" />
                              {new Date(item.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> {item.agent_name}</span>
                          {item.lieu && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.lieu}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Detentions (Gardes à vue) */}
          <TabsContent value="detentions">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground">Chargement...</div>
                ) : filterItems(activeDetentions).length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <HandMetal className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    {language === 'fr' ? 'Aucune garde à vue active' : 'No active detentions'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filterItems(activeDetentions).map((det) => {
                      const isOverdue = det.date_fin_prevue && new Date(det.date_fin_prevue) < new Date();
                      return (
                        <div key={det.detention_id} className={`p-4 hover:bg-muted/50 ${isOverdue ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`} data-testid={`det-${det.detention_id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${det.confirmed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                                <HandMetal className={`w-5 h-5 ${det.confirmed ? 'text-green-600' : 'text-orange-600'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{det.person_name}</p>
                                  {det.confirmed ? (
                                    <Badge className="bg-green-100 text-green-700 text-[10px]">Confirmée</Badge>
                                  ) : (
                                    <Badge className="bg-amber-100 text-amber-700 text-[10px]">Non confirmée</Badge>
                                  )}
                                  {isOverdue && <Badge className="bg-red-500 text-white text-[10px]">Dépassée</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground">{det.motif} | {det.duree_heures}h</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {isSupervisor && !det.confirmed && (
                                <Button size="sm" onClick={() => handleConfirm(det.detention_id)} className="gap-1 bg-green-600 hover:bg-green-700" data-testid={`confirm-${det.detention_id}`}>
                                  <CheckCircle className="w-3 h-3" />
                                  Confirmer
                                </Button>
                              )}
                              {isSupervisor && (
                                <Button size="sm" variant="destructive" onClick={() => handleRelease(det.detention_id, det.person_name)} className="gap-1" data-testid={`release-${det.detention_id}`}>
                                  <Unlock className="w-3 h-3" />
                                  Libérer
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> {det.agent_name}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Fin prévue: {det.date_fin_prevue ? new Date(det.date_fin_prevue).toLocaleString('fr-FR') : '-'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Libérations */}
          <TabsContent value="liberations">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground">Chargement...</div>
                ) : filterItems(releasedDetentions).length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Unlock className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    {language === 'fr' ? 'Aucune libération' : 'No releases'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filterItems(releasedDetentions).map((det) => (
                      <div key={det.detention_id} className="p-4 hover:bg-muted/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                              <Unlock className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">{det.person_name}</p>
                              <p className="text-xs text-muted-foreground">{det.motif_liberation || det.motif}</p>
                            </div>
                          </div>
                          <Badge className="bg-green-100 text-green-700 text-[10px]">{det.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Libéré par: {det.libere_par_nom || det.agent_name}</span>
                          <span>{det.date_liberation ? new Date(det.date_liberation).toLocaleString('fr-FR') : ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Police Assistant Modal */}
      <PoliceAssistant
        isOpen={showAssistant}
        onClose={() => setShowAssistant(false)}
        onActionComplete={() => loadData()}
      />
    </DashboardLayout>
  );
};

export default PoliceWorkPage;
